'use client';

import { useMemo, useState } from 'react';
import { Wallet, ArrowDownLeft, ArrowUpRight, Plus, Trash2, X, TriangleAlert, HandCoins, Undo2 } from 'lucide-react';
import type { Booking, Vehicle } from '@/types';
import { api, ApiError } from '@/lib/api';
import { daysUntil, complianceState } from '@/lib/morocco';
import { currencySymbol, formatMoney } from '@/lib/currency';
import { parseBookingPage } from '@/lib/booking/laravel-mirror';
import { parseCashPage } from '@/lib/ledger/ledger-catalog';
import { listPath } from '@/lib/query/page';
import { useInvalidateTenantResource } from '@/lib/query/scope';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { StatusChip } from './desk-feedback';

export interface CashMovement {
  id: string;
  /** ISO day YYYY-MM-DD */
  date: string;
  type: 'in' | 'out';
  category: 'avance' | 'solde' | 'caution_rendue' | 'caution_recue' | 'depense' | 'autre';
  label: string;
  amount: number;
  bookingId?: string;
}

interface CashModuleProps {
  currency?: string | null;
  headerQuery?: string;
}

const CATEGORY_LABELS: Record<CashMovement['category'], string> = {
  avance: 'Avance', solde: 'Solde', caution_rendue: 'Caution rendue',
  caution_recue: 'Caution reçue', depense: 'Dépense', autre: 'Autre',
};

type BookingOption = Pick<Booking, 'id' | 'customerName' | 'vehicleName' | 'vehicleId'> & {
  vehicle?: Pick<Vehicle, 'id' | 'name' | 'licensePlate'>;
};

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function uid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

const inputCls = 'w-full rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-brand';
const labelCls = 'mb-1 block text-xs font-medium text-muted';

export function CashModule({ currency, headerQuery = '' }: CashModuleProps) {
  const cur = currency ?? 'MAD';
  const invalidate = useInvalidateTenantResource();
  const [selectedDate, setSelectedDate] = useState<string>(() => todayISO());
  const cashQuery = useTenantQuery(
    ['cash', selectedDate, headerQuery],
    listPath('/api/cash', { perPage: 50, date: selectedDate, q: headerQuery.trim() }),
    parseCashPage,
  );
  const movements = (cashQuery.data?.data ?? []) as CashMovement[];
  const dayBookings = useTenantQuery(
    ['bookings', 'cash-day', selectedDate],
    listPath('/api/bookings', { from: selectedDate, to: selectedDate, perPage: 50 }),
    parseBookingPage,
  );
  const pendingPage = useTenantQuery(
    ['bookings', 'cash-pending'],
    '/api/bookings?status=pending&perPage=50',
    parseBookingPage,
  );
  const returnedPage = useTenantQuery(
    ['bookings', 'cash-returned'],
    '/api/bookings?status=returned&perPage=50',
    parseBookingPage,
  );
  const dueToday = (dayBookings.data?.data ?? []) as Booking[];
  const pendingRows = (pendingPage.data?.data ?? []) as Booking[];
  const returnedRows = (returnedPage.data?.data ?? []) as Booking[];
  const [showAdd, setShowAdd] = useState(false);
  const [fType, setFType] = useState<'in' | 'out'>('in');
  const [fCategory, setFCategory] = useState<CashMovement['category']>('avance');
  const [fLabel, setFLabel] = useState('');
  const [fAmount, setFAmount] = useState('');
  const [fBookingId, setFBookingId] = useState('');

  const dayMovements = useMemo(() => {
    const needle = headerQuery.trim().toLowerCase();
    return movements.filter((movement) => {
      if (movement.date.slice(0, 10) !== selectedDate) return false;
      if (needle === '') return true;
      return `${movement.label} ${movement.category} ${movement.bookingId ?? ''}`.toLowerCase().includes(needle);
    });
  }, [movements, selectedDate, headerQuery]);
  const totalIn = useMemo(() => dayMovements.filter((m) => m.type === 'in').reduce((s, m) => s + m.amount, 0), [dayMovements]);
  const totalOut = useMemo(() => dayMovements.filter((m) => m.type === 'out').reduce((s, m) => s + m.amount, 0), [dayMovements]);
  const solde = totalIn - totalOut;

  // Attendu du jour : avances + soldes des dossiers dont le retrait est prévu ce jour-là.
  const expected = useMemo(() => {
    const due = dueToday.filter((b) => b.status !== 'cancelled' && b.pickupDate === selectedDate);
    const avance = due.reduce((s, b) => s + (b.avanceAmount ?? 0), 0);
    const soldeDue = due.reduce((s, b) => s + (b.totalPrice - (b.avanceAmount ?? 0)), 0);
    return { avance, soldeDue, total: avance + soldeDue, count: due.length };
  }, [dueToday, selectedDate]);
  const ecart = totalIn - expected.total;

  const pendingAvances = useMemo(() => pendingRows.filter((b) => (b.avanceAmount ?? 0) > 0), [pendingRows]);
  const returnedCautions = useMemo(() => returnedRows.filter((b) => (b.cautionAmount ?? 0) > 0), [returnedRows]);

  const hasMovement = (bookingId: string, category: CashMovement['category']) =>
    movements.some((m) => m.bookingId === bookingId && m.category === category && m.date.slice(0, 10) === selectedDate);

  async function saveMovement(row: Omit<CashMovement, 'id'>) {
    try {
      await api('/api/cash', { method: 'POST', body: JSON.stringify(row) });
      await invalidate('cash');
    } catch (error: unknown) {
      window.alert(error instanceof ApiError ? error.message : 'Could not save this cash line.');
    }
  }
  function quickAdd(b: Booking, type: 'in' | 'out', category: CashMovement['category'], amount: number, prefix: string) {
    if (amount <= 0 || hasMovement(b.id, category)) return;
    void saveMovement({ date: selectedDate, type, category, label: `${prefix} ${b.id} — ${b.customerName}`, amount, bookingId: b.id });
  }
  function removeMovement(id: string) {
    void api(`/api/cash/${id}`, { method: 'DELETE' }).then(() => invalidate('cash')).catch(() => {});
  }
  function submitAdd() {
    const amount = Number(fAmount);
    if (!fLabel.trim() || !Number.isFinite(amount) || amount <= 0) return;
    const opt: BookingOption | undefined = fBookingId
      ? [...dueToday, ...pendingRows, ...returnedRows].find((b) => b.id === fBookingId)
      : undefined;
    void saveMovement({ date: selectedDate, type: fType, category: fCategory, label: fLabel.trim(), amount: Math.round(amount), bookingId: opt?.id });
    setShowAdd(false); setFLabel(''); setFAmount(''); setFBookingId(''); setFType('in'); setFCategory('avance');
  }
  function pickupBadge(b: Booking) {
    if (complianceState(b.pickupDate) === 'expired')
      return <StatusChip tone="danger">En retard</StatusChip>;
    const d = daysUntil(b.pickupDate);
    if (b.pickupDate === selectedDate || d === 0 || d === 1)
      return <StatusChip tone="warning">Aujourd&apos;hui</StatusChip>;
    return <span className="rounded-full bg-shell px-2 py-0.5 text-xs text-muted">{b.pickupDate}</span>;
  }

  const quickBtn = 'shrink-0 rounded-xl bg-ink px-3 py-1.5 text-xs font-semibold text-shell hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-ink text-shell"><Wallet className="h-4 w-4" /></span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-bold text-ink">Caisse journalière</h2>
            <p className="truncate text-xs text-muted">Encaissements, décaissements et clôture du jour</p>
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-brand" />
          <button type="button" onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 rounded-xl bg-ink px-3 py-2 text-xs font-semibold text-shell hover:brightness-110">
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
      </div>

      {/* Clôture du jour */}
      <div className="rounded-3xl border border-line bg-card p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-muted">Clôture du jour — {selectedDate}</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-2xl bg-success/10 p-3">
            <p className="flex items-center gap-1 text-xs text-success"><ArrowDownLeft className="h-3.5 w-3.5" /> Encaissé</p>
            <p className="mt-1 text-sm font-bold text-success">{formatMoney(totalIn, cur)}</p>
          </div>
          <div className="rounded-2xl bg-danger/10 p-3">
            <p className="flex items-center gap-1 text-xs text-danger"><ArrowUpRight className="h-3.5 w-3.5" /> Décaissé</p>
            <p className="mt-1 text-sm font-bold text-danger">{formatMoney(totalOut, cur)}</p>
          </div>
          <div className="rounded-2xl bg-ink p-3 text-shell">
            <p className="text-xs text-shell/70">Solde caisse du jour</p>
            <p className="mt-1 text-sm font-bold">{formatMoney(solde, cur)}</p>
          </div>
          <div className="rounded-2xl bg-shell p-3">
            <p className="text-xs text-muted">Attendu ({expected.count} dossier{expected.count > 1 ? 's' : ''})</p>
            <p className="mt-1 text-sm font-bold text-ink">{formatMoney(expected.total, cur)}</p>
            <p className="text-xs text-muted">Avances {formatMoney(expected.avance, cur)} · Soldes {formatMoney(expected.soldeDue, cur)}</p>
          </div>
        </div>
        {ecart !== 0 ? (
          <div className="mt-3 flex items-center gap-2 rounded-2xl border border-warning/30 bg-warning/10 px-3 py-2">
            <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
            <p className="text-xs text-warning">Écart de <span className="font-bold">{formatMoney(ecart, cur)}</span> entre l&apos;encaissé ({formatMoney(totalIn, cur)}) et l&apos;attendu ({formatMoney(expected.total, cur)}). Vérifiez le journal ci-dessous.</p>
          </div>
        ) : (
          <div className="mt-3 rounded-2xl bg-success/10 px-3 py-2">
            <p className="text-xs text-success">Caisse équilibrée : encaissé conforme à l&apos;attendu du jour.</p>
          </div>
        )}
      </div>

      {/* Actions rapides */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl border border-line bg-card p-4">
          <p className="flex items-center gap-1.5 text-xs font-bold text-ink"><HandCoins className="h-4 w-4 text-[#FF8A00]" /> Encaisser avance</p>
          <p className="mt-0.5 text-xs text-muted">Dossiers en attente avec avance prévue</p>
          <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">
            {pendingAvances.length === 0 && <p className="text-xs text-muted">Aucune avance à encaisser.</p>}
            {pendingAvances.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 rounded-2xl bg-shell px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-ink">{b.customerName} — {b.vehicleName}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs font-bold text-success">{formatMoney(b.avanceAmount ?? 0, cur)}</span>{pickupBadge(b)}
                  </div>
                </div>
                <button type="button" disabled={hasMovement(b.id, 'avance')} onClick={() => quickAdd(b, 'in', 'avance', b.avanceAmount ?? 0, 'Avance')} className={quickBtn}>
                  {hasMovement(b.id, 'avance') ? 'Encaissée' : 'Encaisser'}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-line bg-card p-4">
          <p className="flex items-center gap-1.5 text-xs font-bold text-ink"><Undo2 className="h-4 w-4 text-[#FF8A00]" /> Rendre caution</p>
          <p className="mt-0.5 text-xs text-muted">Dossiers retournés avec caution à restituer</p>
          <div className="mt-3 max-h-56 space-y-2 overflow-y-auto">
            {returnedCautions.length === 0 && <p className="text-xs text-muted">Aucune caution à rendre.</p>}
            {returnedCautions.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 rounded-2xl bg-shell px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-ink">{b.customerName} — {b.vehicleName}</p>
                  <p className="mt-1 text-xs font-bold text-danger">{formatMoney(b.cautionAmount ?? 0, cur)}</p>
                </div>
                <button type="button" disabled={hasMovement(b.id, 'caution_rendue')} onClick={() => quickAdd(b, 'out', 'caution_rendue', b.cautionAmount ?? 0, 'Caution rendue')} className={quickBtn}>
                  {hasMovement(b.id, 'caution_rendue') ? 'Rendue' : 'Rendre'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Journal */}
      <div className="overflow-hidden rounded-3xl border border-line bg-card">
        <div className="border-b border-line px-4 py-3">
          <p className="text-xs font-bold text-ink">Journal du {selectedDate}</p>
          <p className="text-xs text-muted">{dayMovements.length} mouvement{dayMovements.length > 1 ? 's' : ''}</p>
        </div>
        {dayMovements.length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted">Aucun mouvement pour ce jour.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-shell text-left text-muted">
                  <th className="px-4 py-2 font-medium">Sens</th><th className="px-4 py-2 font-medium">Catégorie</th><th className="px-4 py-2 font-medium">Libellé</th><th className="px-4 py-2 font-medium">Dossier</th><th className="px-4 py-2 text-right font-medium">Montant</th><th className="px-4 py-2 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {dayMovements.map((m) => (
                  <tr key={m.id} className="border-t border-line">
                    <td className="px-4 py-2">
                      <StatusChip tone={m.type === 'in' ? 'success' : 'danger'}>{m.type === 'in' ? 'Entrée' : 'Sortie'}</StatusChip>
                    </td>
                    <td className="px-4 py-2 text-muted">{CATEGORY_LABELS[m.category]}</td>
                    <td className="px-4 py-2 font-medium text-ink">{m.label}</td>
                    <td className="px-4 py-2 text-muted">{m.bookingId ? `#${m.bookingId.slice(0, 8)}` : '—'}</td>
                    <td className={`px-4 py-2 text-right font-bold ${m.type === 'in' ? 'text-success' : 'text-danger'}`}>{m.type === 'in' ? '+' : '−'}{formatMoney(m.amount, cur)}</td>
                    <td className="px-4 py-2 text-right">
                      <button type="button" onClick={() => removeMovement(m.id)} title="Supprimer" className="rounded-lg p-1.5 text-muted hover:bg-danger/10 hover:text-danger"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modale d'ajout */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-md rounded-3xl border border-line bg-card p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-ink">Nouveau mouvement — {selectedDate}</p>
              <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg p-1.5 text-muted hover:bg-shell"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Sens</label>
                <select value={fType} onChange={(e) => setFType(e.target.value as 'in' | 'out')} className={inputCls}>
                  <option value="in">Entrée</option><option value="out">Sortie</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Catégorie</label>
                <select value={fCategory} onChange={(e) => setFCategory(e.target.value as CashMovement['category'])} className={inputCls}>
                  {(Object.keys(CATEGORY_LABELS) as CashMovement['category'][]).map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>Libellé</label>
              <input value={fLabel} onChange={(e) => setFLabel(e.target.value)} placeholder="Ex : Avance dossier…" className={inputCls} />
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Montant ({currencySymbol(cur)})</label>
                <input type="number" min="0" value={fAmount} onChange={(e) => setFAmount(e.target.value)} placeholder="0" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Dossier (optionnel)</label>
                <select value={fBookingId} onChange={(e) => setFBookingId(e.target.value)} className={inputCls}>
                  <option value="">— Aucun —</option>
                  {[...dueToday, ...pendingRows, ...returnedRows]
                    .filter((booking, index, rows) => rows.findIndex((row) => row.id === booking.id) === index)
                    .map((b) => <option key={b.id} value={b.id}>{b.id.slice(0, 8)} · {b.customerName}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="rounded-xl border border-line px-3 py-2 text-xs font-semibold text-muted hover:bg-shell">Annuler</button>
              <button type="button" onClick={submitAdd} className="rounded-xl bg-ink px-4 py-2 text-xs font-semibold text-shell hover:brightness-110">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
